import { useContext, useMemo } from 'react';
import styles from './CompositionStructureView.module.css';
import ThemeContext from 'config/context/themeContext';
import { useCompositionsContext } from 'features/compositions/context/CompositionsContext';
import { ExportIcon } from 'assets/icons';
import downloadFile from 'lib/DownloadFile';

const getDisplayType = (field) => {
  if (field?.is_object) {
    return field?.object?.name || field?.type;
  }
  return field?.type;
};

const ObjectFieldRenderer = ({ field, level = 0 }) => {
  const indentStyle = {
    paddingLeft: `${level * 20 + 10}px`
  };

  return (
    <>
      <div className={styles.row} style={indentStyle}>
        <div className={styles.cell_name}>
          {field.name}
          {field.is_array && <span className={styles.array_badge}>ARRAY</span>}
        </div>
        <div className={styles.cell_type}>
          {getDisplayType(field)}
          {field.is_object && <span className={styles.object_badge}>OBJECT</span>}
        </div>
      </div>
      {field.is_object && field.object && field.object.fields && (
        <div className={styles.object_structure}>
          {field.object.fields.map((nestedField, idx) => (
            <ObjectFieldRenderer
              key={`${nestedField.name}-${idx}`}
              field={nestedField}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </>
  );
};

const CompositionStructureView = () => {
  const { dark } = useContext(ThemeContext);
  const { selectedComposition } = useCompositionsContext();

  const handleExportStructure = () => {
    if (!selectedComposition?.structure) return;
    const compositionName = selectedComposition?.name || 'composition';
    const safeName = compositionName.replace(/[^a-zA-Z0-9._-]/g, '_');
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `${safeName}_structure_${timestamp}.json`;
    const content = JSON.stringify(selectedComposition.structure, null, 2);
    downloadFile(content, filename, { type: 'application/json;charset=utf-8;' });
  };

  const rows = useMemo(() => {
    const idx = (selectedComposition?.structure?.indexes || []).map(i => ({
      name: i.name,
      type: i.type,
      kind: 'index'
    }));
    const flds = (selectedComposition?.structure?.fields || []).map(f => ({
      ...f,
      kind: 'field'
    }));
    return [...idx, ...flds];
  }, [selectedComposition?.structure]);

  return (
    <div className={`${styles.composition_structure_view} ${dark ? styles.dark : ''}`}>
      <div className={styles.title_row}>
        <div className={styles.component_title}>Composition Structure</div>
        <button
          type="button"
          className={styles.export_button}
          onClick={handleExportStructure}
          disabled={!selectedComposition?.structure}
          title="Export structure JSON"
        >
          <ExportIcon />
          Export JSON
        </button>
      </div>
      <div className={styles.composition_structure_scroll}>
        <div className={styles.composition_structure}>
          <div className={styles.composition_structure_header}>
            <span>NAME</span>
            <span>TYPE</span>
          </div>
          <div className={styles.composition_structure_body}>
            {rows.map((r, i) => {
              if (r.kind === 'index') {
                return (
                  <div className={styles.row} key={`${r.kind}-${r.name}-${i}`}>
                    <div className={styles.cell_name}>
                      {r.name}
                      <span className={styles.index_badge}>INDEX</span>
                    </div>
                    <div className={styles.cell_type}>{r.type}</div>
                  </div>
                );
              } else {
                return (
                  <ObjectFieldRenderer
                    key={`${r.kind}-${r.name}-${i}`}
                    field={r}
                    level={0}
                  />
                );
              }
            })}
            {rows.length === 0 && (
              <div className={styles.empty}>No structure to display.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompositionStructureView;